import { useState } from 'react'
import { Plus, TrendingUp, ArrowUp, ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertBanner } from '@/components/ui/Feedback/AlertBanner'
import { SwipeableLeadCard } from './SwipeableLeadCard'
import { useMobileResponsive } from '@/hooks/useMobileResponsive'
import { useRealTimeAlerts } from '@/hooks/useRealTimeAlerts'
import { cn } from '@/lib/utils'
import type { Lead } from '@/services/mockData'
import type { LeadTableRow } from '@/services/mockLeads'

interface MobileDashboardProps {
  leads: LeadTableRow[]
  onApprove: (leadId: string) => void
  onReject: (leadId: string) => void
  onViewDetails: (leadId: string) => void
}

interface MetricCardProps {
  label: string
  value: string | number
  trend?: {
    direction: 'up' | 'down'
    percentage: number
  }
  className?: string
}

function MetricCard({ label, value, trend, className }: MetricCardProps) {
  return (
    <Card className={cn("min-w-[140px] bg-card", className)}>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
        {trend && (
          <div className={cn(
            "flex items-center gap-1 text-xs mt-1",
            trend.direction === 'up' ? "text-green-600" : "text-red-600"
          )}>
            {trend.direction === 'up' ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
            <span>{trend.percentage}%</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function MobileDashboard({ 
  leads, 
  onApprove, 
  onReject, 
  onViewDetails 
}: MobileDashboardProps) {
  const [quickActionsOpen, setQuickActionsOpen] = useState(false)
  const { isMobile } = useMobileResponsive()
  const { alerts, criticalAlerts } = useRealTimeAlerts()

  const todayMetrics = [
    { label: "Today's Leads", value: 247, trend: { direction: 'up' as const, percentage: 12 } },
    { label: "Blocked", value: 79, trend: { direction: 'up' as const, percentage: 8 } },
    { label: "Saved", value: "$2,370" },
    { label: "Accuracy", value: "95.3%" }
  ]

  if (!isMobile) {
    return null // Only render on mobile
  }

  return (
    <div className="w-full">
      {/* Critical Alert Banner */}
      {criticalAlerts > 0 && (
        <div className="mx-0 mb-4">
          <AlertBanner
            type="critical"
            title="🚨 Sequential Attack Detected"
            message={`${criticalAlerts} fraudulent leads blocked in the last 5 minutes`}
            dismissible
            className="rounded-none border-x-0"
          />
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Key Metrics - Horizontal Scroll */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Today's Overview</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {todayMetrics.map((metric, index) => (
              <MetricCard
                key={index}
                label={metric.label}
                value={metric.value}
                trend={metric.trend}
              />
            ))}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Leads</h2>
            <Button variant="ghost" size="sm">
              View All
            </Button>
          </div>

          <div className="space-y-3">
            {leads.slice(0, 5).map((lead) => (
              <SwipeableLeadCard
                key={lead.id}
                lead={lead}
                onApprove={onApprove}
                onReject={onReject}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>

          {leads.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="text-muted-foreground mb-2">No recent leads</div>
                <div className="text-sm text-muted-foreground">
                  New leads will appear here as they come in
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                47ms
              </div>
              <div className="text-sm text-muted-foreground">
                Avg Detection Time
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">System Live</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                All services operational
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-20 right-4 z-30">
        <div className="relative">
          <Button
            size="lg"
            className="w-14 h-14 rounded-full shadow-lg"
            onClick={() => setQuickActionsOpen(!quickActionsOpen)}
          >
            <Plus className={cn(
              "h-6 w-6 transition-transform",
              quickActionsOpen && "rotate-45"
            )} />
          </Button>

          {/* Quick Action Menu */}
          {quickActionsOpen && (
            <div className="absolute bottom-16 right-0 space-y-2">
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2 whitespace-nowrap"
                onClick={() => {
                  // Handle export
                  setQuickActionsOpen(false)
                }}
              >
                📊 Export Data
              </Button>
              
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2 whitespace-nowrap"
                onClick={() => {
                  // Handle add rule
                  setQuickActionsOpen(false)
                }}
              >
                ⚙️ Add Rule
              </Button>
              
              <Button
                size="sm"
                variant="secondary"
                className="flex items-center gap-2 whitespace-nowrap"
                onClick={() => {
                  // Handle view alerts
                  setQuickActionsOpen(false)
                }}
              >
                🔔 View Alerts
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}